/* eslint-disable consistent-return */
import { merge } from 'lodash';
import LocaleEnUs from '../locale/en_US';
import generateDefaultStyles from '../style/default';

/**
 * Handles loading and initializing charts, either from a JS object or a JSON string
 */
export class ChartLoader {
	/**
	 * Creates an instance of ChartLoader
	 * @param {object} chart - The chart instance to be managed
	 */
	constructor(chart) {
		this.chart = chart;
	}

	/**
	 * Loads a chart using parameters or a saved state
	 * @param {string|object} newParams - The new parameters or a saved state string
	 * @param {Function} callback - Callback function to execute after loading
	 * @param {object} args - Additional arguments, defaults to { loadData: true }
	 * @returns The chart instance for chaining
	 */
	load(newParams, callback, args = { loadData: true }) {
		// if it's a string, it's a previously saved chart coming from the database
		// if it's a JS object, it's a new chart coming from the webcode
		const isStringInput = typeof newParams === 'string';
		const newChart = isStringInput ? JSON.parse(newParams) : newParams;

		if (!this._isValidChart(newChart)) {
			return this.chart.showMessageChartNotAvailable({
				description: 'Invalid chart inputs'
			});
		}

		this.chart.state.updateXAxisPresentScaleDomain = this._needsXAxisUpdate(newChart);

		this._prepareChartForLoading(newChart);
		this._buildPanels(newChart);

		if (args.loadData) {
			this._loadData(callback, args);
		}

		return this.chart;
	}

	/**
	 * Checks if the provided chart object is valid
	 * @param {object} chart - The chart object to validate
	 * @returns {boolean} True if the chart is valid, otherwise false
	 */
	_isValidChart(chart) {
		return !!chart?.params;
	}

	/**
	 * Determines if the X-axis needs updating based on the saved chart's parameters
	 * @param {object} newChart - The new chart object
	 * @returns {boolean} True if an update is needed, otherwise false
	 */
	_needsXAxisUpdate(newChart) {
		const params = newChart.params;
		return Object.prototype.hasOwnProperty.call(params, 'dateStart') &&
		Object.prototype.hasOwnProperty.call(params, 'dateStop');
	}

	/**
	 * Prepares the chart for loading by setting parameters, locale, and styles
	 * @param {object} newChart - The new chart object
	 */
	_prepareChartForLoading(newChart) {
		this.chart.removePanels();
		this.chart.params = this.chart.getDefaultParams(newChart.params || {});
		this.chart.setLocale(merge({}, LocaleEnUs, this.chart.params.locale, this.chart.locale || {}));
		this.chart.styleMap = this._generateStyleMap(this.chart.params);
		this._convertDateParamsToObjects();
	}

	/**
	 * Generates a style map based on the given parameters
	 * @param {object} params - Chart parameters
	 * @returns {object} The generated style map
	 */
	_generateStyleMap(params) {
		const defaultStyles = generateDefaultStyles(params.styleCommon);
		return merge({}, defaultStyles, params.style);
	}

	/**
	 * Converts string date parameters to Date objects
	 */
	_convertDateParamsToObjects() {
		if (this.chart.params.dateStart && this.chart.params.dateStop) {
			this.chart.params.dateStart = this._convertToDate(this.chart.params.dateStart);
			this.chart.params.dateStop = this._convertToDate(this.chart.params.dateStop);
		}
	}

	/**
	 * Converts a string to a Date object, removing any unwanted characters
	 * @param {string} dateString - The date string to convert
	 * @returns {Date} The converted Date object
	 */
	_convertToDate(dateString) {
		if (typeof dateString === 'string') {
			dateString = dateString.replace(/"/g, '');
		}
		return new Date(dateString);
	}

	/**
	 * Build the panels from the new chart configuration
	 * @param {object} newChart - The new chart object
	 */
	_buildPanels(newChart) {
		this._validatePanelHeights(newChart);
		newChart.panels.forEach((savedPanel, index) => {
			const panel = this.chart.addPanel(savedPanel.params);
			if (index === 0 && this.chart.params.yAxisScale) {
				panel.setScale(this.chart.params.yAxisScale);
			}
			this._buildPanelIndicators(panel, savedPanel);
			this._buildPanelTools(panel, savedPanel);
			this._buildPanelEvents(panel, savedPanel);
		});
		this._updateChartSize();
		if (newChart.metaData) {
			this.chart.metaData = { ...newChart.metaData };
		}
		this._handleHiddenLowerPanels(newChart);
	}

	/**
	 * Validates the total height percentage of all panels
	 * Ensures the sum does not exceed 100%
	 * @param {object} newChart - The new chart object
	 */
	_validatePanelHeights(newChart) {
		let heightPctSum = 0;
		newChart.panels.forEach(p => {
			if (p.params?.size && typeof p.params.size.heightPct === 'number') {
				heightPctSum += p.params.size.heightPct;
			}
		});

		// this has happened in rare cases with saved charts where the sum is slightly greater than 1
		if (heightPctSum > 1) {
			newChart.panels.forEach(p => {
				p.params.size.heightPct = null;
			});
			this.chart.warn('Total panel height percentage is greater than 100%. Using fallback values.');
		}
	}

	/**
	 * Restores panel indicators from the saved panel data
	 * @param {object} panel - The panel to restore indicators to
	 * @param {object} savedPanel - The saved panel data
	 */
	_buildPanelIndicators(panel, savedPanel) {
		const indicators = savedPanel.indicators.map(indicator => ({ ...indicator }));
		panel.addIndicators(indicators);
	}

	/**
	 * Restores panel tools from the saved panel data
	 * @param {object} panel - The panel to restore tools to
	 * @param {object} savedPanel - The saved panel data
	 */
	_buildPanelTools(panel, savedPanel) {
		panel.tools = this.chart.getToolsFromJSON(panel, savedPanel.tools);
	}

	/**
	 * Restores panel events from the saved panel data
	 * @param {object} panel - The panel to restore events to
	 * @param {object} savedPanel - The saved panel data
	 */
	_buildPanelEvents(panel, savedPanel) {
		const events = savedPanel.events || [];
		events.forEach(event => {
			const eventParams = event.params;
			panel.addEvent(event.id || eventParams.id, eventParams || {});
		});
	}

	/**
	 * Updates the size of the chart based on the loaded panels
	 */
	_updateChartSize() {
		this.chart.setChartSize();
		if (!this.chart.params.size) {
			this.chart.rootMouse.style('width', `${this.chart.size.width}px`);
			this.chart.rootMouse.style('height', `${this.chart.size.height}px`);
		}
	}

	/**
	 * Handles hidden lower panels based on the saved chart configuration
	 * Note: currently only used on mobile.
	 * @param {object} newChart - The new chart object
	 */
	_handleHiddenLowerPanels(newChart) {
		if (newChart.params.hiddenLowers && this.chart.updateLowerPanels) {
			this.chart.updateLowerPanels(newChart.params.hiddenLowers);
		}
	}

	/**
	 * Loads data into the chart based on the provided parameters
	 * @param {Function} callback - Optional callback function to execute after data is loaded
	 */
	_loadData(callback, args = {}) {
		if (this.chart.params.customData) {
			this.chart.loadCustomData(callback, { loadSaved: true });
		} else {
			this.chart.loadData(callback, { loadSaved: true, ...args });
		}
	}
}
